import { Edit, Person } from '@mui/icons-material';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography
} from '@mui/material';
import { showAlert, validatePermissions } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { getAvailableAnalysts, updateRequestAnalyst } from 'lib/api/financing';
import { SelectInput } from 'lib/common/Inputs';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';

const PERMISSION_MAP = {
  'factoring::risk': 'be_factoring_risk_analyst',
  'factoring::operations': 'be_factoring_operations_analyst',
  'confirming::risk': 'be_confirming_risk_analyst',
  'confirming::operations': 'be_confirming_operations_analyst',
  'credit::risk': 'be_credit_request_risk_analyst',
  'credit::operations': 'be_credit_request_operations_analyst',
};

export type Executive = {
  id: number;
  name: string | null;
  email: string | null;
};

type AnalystRowProps = {
  executive: Executive | null;
  analystType: 'risk' | 'operations';
  requestId: number;
  requestType: 'factoring' | 'confirming' | 'credit';
  otherAnalystId: number | null;
}

const AnalystRow: React.FC<AnalystRowProps> = ({
  executive,
  analystType,
  requestId,
  requestType,
  otherAnalystId,
}) => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [selectedAnalyst, setSelectedAnalyst] = useState<string>(
    executive?.id ? String(executive.id) : 'Sin asignar',
  );

  const permissionForEditing = PERMISSION_MAP[`${requestType}::${analystType}` as keyof typeof PERMISSION_MAP];
  if (!permissionForEditing) return null;
  const canEdit = validatePermissions(user, permissionForEditing);
  const hasExecutive = executive !== null;
  const executiveName = hasExecutive ? executive.name : 'Sin nombre';
  const executiveEmail = hasExecutive ? executive.email : 'Sin email';

  const selectedAnalystId = selectedAnalyst === 'Sin asignar' ? null : Number(selectedAnalyst);
  const { mutate: updateRequestAnalystMutation, isPending: isLoadingUpdateRequestAnalyst } = updateRequestAnalyst(
    dispatch,
    requestId,
    requestType,
  );

  const { data, isLoading: isLoadingAvailableAnalysts } = getAvailableAnalysts(
    dispatch,
    requestType,
    analystType,
    editing,
  );
  const availableAnalysts = data?.analysts || [];
  const availableOptions: { value: string; label: string }[] = [
    { value: 'Sin asignar', label: 'Sin asignar' },
    ...availableAnalysts.map((analyst) => ({
      value: String(analyst.id),
      label: analyst.name || '',
    })),
  ];

  const handleEdit = () => {
    setEditing(true);
  };

  const handleSave = () => {
    const body = {
      requestType,
      requestId,
      riskAnalystId: analystType === 'risk' ? selectedAnalystId : otherAnalystId,
      operationsAnalystId: analystType === 'operations' ? selectedAnalystId : otherAnalystId,
    };
    updateRequestAnalystMutation(body, {
      onSuccess: () => {
        setEditing(false);
        showAlert(dispatch, 'success', 'Analista actualizado correctamente');
      },
      onError: () => {
        setEditing(false);
        showAlert(dispatch, 'error', 'Error al actualizar el analista.');
      },
    });
  };

  const loadingEdit = isLoadingAvailableAnalysts || isLoadingUpdateRequestAnalyst;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            backgroundColor: hasExecutive
              ? 'rgba(25, 118, 210, 0.1)'
              : 'rgba(158, 158, 158, 0.1)',
            borderRadius: '50%',
            p: 0.4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '24px',
            height: '24px',
            flexShrink: 0,
          }}
        >
          <Person
            sx={{
              fontSize: '0.8rem',
              color: hasExecutive ? 'primary.main' : 'text.disabled',
            }}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Chip
              label={analystType === 'risk' ? 'Analista riesgo' : 'Analista operaciones'}
              size="small"
              color={analystType === 'risk' ? 'primary' : 'warning'}
              variant="outlined"
              sx={{ height: '16px', fontSize: '0.6rem', mb: 0.2 }}
            />
            {hasExecutive ? (
              <>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
                >
                  {executiveName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.65rem', display: 'block' }}
                >
                  {executiveEmail}
                </Typography>
              </>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                  color: 'text.disabled',
                  fontStyle: 'italic',
                }}
              >
                Sin asignar
              </Typography>
            )}
          </Box>
          {canEdit && (
            <IconButton
              size="small"
              onClick={handleEdit}
              sx={{
                padding: 0.75,
                color: 'primary.main',
                flexShrink: 0,
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.16)',
                },
              }}
            >
              <Edit sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          )}
        </Box>
      </Paper>
      <Dialog open={editing} onClose={() => setEditing(false)}>
        <DialogTitle>Editar Analista {analystType === 'risk' ? 'Riesgo' : 'Operaciones'}</DialogTitle>
        <DialogContent>
          {loadingEdit ? (
            <div style={{ display: 'flex', justifyContent: 'center', minHeight: '50px' }}>
              <CenteredCircularProgress type="dialog" />
            </div>
          ) : (
            <Box sx={{ mb: 2, p: 2 }}>
              <SelectInput
                label="Analista"
                options={availableOptions}
                value={selectedAnalyst}
                onChange={(value) =>
                  setSelectedAnalyst(value ?? 'Sin asignar')
                }
              />
            </Box>
          )}
          <DialogActions>
            <CustomButton color="secondary" onClick={() => setEditing(false)}>Cancelar</CustomButton>
            <CustomButton color="primary" onClick={() => handleSave()}>Guardar</CustomButton>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnalystRow;
